import type { ApiResponse } from "../../../services/genesisApi/httpsMethods";
import type { DealHeroImageResponse } from "../../../types/dealImages";

/**
 * Hero image URLs are short-lived SAS links (the API signs them for 60
 * minutes), so cached entries are refetched well before they expire.
 */
export const HERO_PREVIEW_TTL_MS = 50 * 60 * 1000;

type HeroPreviewEntry = { url: string | null; fetchedAt: number };

export type HeroPreviewFetcher = (
  dealId: number,
) => Promise<ApiResponse<DealHeroImageResponse>>;

/**
 * Per-deal cache of hero preview URLs with expiry and in-flight de-duplication.
 * Only successful responses are cached (including "deal has no hero image");
 * failed or rejected requests resolve to `null` but are retried next time.
 */
export function createHeroPreviewCache(
  fetcher: HeroPreviewFetcher,
  now: () => number = Date.now,
  ttlMs: number = HERO_PREVIEW_TTL_MS,
) {
  const cache = new Map<number, HeroPreviewEntry>();
  const inflight = new Map<number, Promise<string | null>>();

  /** Cached URL (or `null` for "no image"); `undefined` when absent or expired. */
  function peek(dealId: number): string | null | undefined {
    const entry = cache.get(dealId);
    if (!entry) return undefined;
    if (now() - entry.fetchedAt >= ttlMs) {
      cache.delete(dealId);
      return undefined;
    }
    return entry.url;
  }

  function get(dealId: number): Promise<string | null> {
    const cached = peek(dealId);
    if (cached !== undefined) return Promise.resolve(cached);

    const existing = inflight.get(dealId);
    if (existing) return existing;

    const request = fetcher(dealId)
      .then((response) => {
        if (!response?.success) return null;
        const url = response.data?.hero?.url || null;
        cache.set(dealId, { url, fetchedAt: now() });
        return url;
      })
      .catch(() => null)
      .finally(() => {
        inflight.delete(dealId);
      });

    inflight.set(dealId, request);
    return request;
  }

  return { peek, get };
}
